import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
const isDefined = (str) => typeof str !== 'undefined' && str !== null;
const filterSelector = (filter) => (f) => {
    if (filter.identifier !== f.identifier)
        return false;
    if (isDefined(filter.min) &&
        isDefined(filter.max) &&
        filter.min === f.min &&
        filter.max === f.max)
        return true;
    if (filter.dateMin &&
        filter.dateMax &&
        filter.dateMin === f.dateMin &&
        filter.dateMax === f.dateMax)
        return true;
    if (filter.value && filter.value === f.value)
        return true;
    return false;
};
export class SearchkitClient {
    constructor({ searchOnLoad = true, itemsPerPage = 10 } = {}) {
        this.query = '';
        this.filters = [];
        this.page = {
            size: itemsPerPage,
            from: 0
        };
        this.sortBy = null;
        this.onSearch = null;
        this.searchOnLoad = searchOnLoad;
    }
    performSearch() {
        if (this.onSearch)
            this.onSearch(this.getVariables());
    }
    getVariables() {
        return {
            query: this.query,
            filters: this.filters,
            page: this.page,
            sortBy: this.sortBy
        };
    }
    setCallbackFn(callback) {
        this.onSearch = callback;
    }
    setQuery(query) {
        this.query = query;
        this.filters = [];
        this.setPage({ from: 0, size: 10 });
    }
    getQuery() {
        return this.query;
    }
    setPage(page) {
        this.page = page;
    }
    search() {
        this.performSearch();
    }
    getFilters() {
        return this.filters;
    }
    canResetSearch() {
        return !(this.filters.length === 0 && !this.query);
    }
    isFilterSelected(filter) {
        const foundFilter = this.filters.find(filterSelector(filter));
        return !!foundFilter;
    }
    getFiltersByIdentifier(identifier) {
        const filters = this.filters.filter((filter) => identifier === filter.identifier);
        return filters.length > 0 ? filters : [];
    }
    removeFilter(filter) {
        this.filters = this.filters.reduce((filters, f) => {
            if (filterSelector(filter)(f)) {
                return [...filters];
            }
            return [...filters, Object.assign({}, f)];
        }, []);
    }
    removeFiltersByIdentifier(identifier) {
        this.filters = this.filters.filter((f) => f.identifier !== identifier);
    }
    addFilter(filter) {
        this.filters = [Object.assign({}, filter), ...this.filters];
    }
    toggleFilter(filter) {
        if (this.isFilterSelected(filter)) {
            this.removeFilter(filter);
        }
        else {
            this.addFilter(filter);
        }
    }
    setSortBy(sort) {
        this.sortBy = sort;
    }
}
export const SearchkitContext = createContext({});
export function SearchkitProvider({ client, children }) {
    return React.createElement(SearchkitContext.Provider, { value: client }, children);
}
export function useSearchkit() {
    const sk = useContext(SearchkitContext);
    return sk;
}
export function useSearchkitQuery(query) {
    const sk = useSearchkit();
    const [variables, setVariables] = useState(sk.getVariables());
    const [execute, state] = useLazyQuery(query, {
        variables: {
            query: variables === null || variables === void 0 ? void 0 : variables.query,
            filters: variables === null || variables === void 0 ? void 0 : variables.filters,
            page: variables === null || variables === void 0 ? void 0 : variables.page,
            sortBy: variables === null || variables === void 0 ? void 0 : variables.sortBy
        }
    });
    useEffect(() => {
        if (sk.searchOnLoad) {
            sk.search();
        }
    }, []);
    sk.setCallbackFn((variables) => {
        setVariables(variables);
        execute();
    });
    return state;
}
